import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { usePlan } from "@/lib/plan";
import { useTenant } from "@/lib/tenant";
import { activeItem, visibleNav, type NavContext } from "./model";
import { REGISTRY } from "./registry";

export function navContext(input: {
  has: (feature: string) => boolean;
  planLoading: boolean;
  permissions: readonly string[];
  role: string;
  isSuperAdmin: boolean;
}): NavContext {
  return {
    // Features read as off until the plan arrives; showing everything for that
    // moment beats a rail that flashes empty and then fills.
    has: input.planLoading ? () => true : input.has,
    permissions: input.permissions,
    role: input.role,
    isSuperAdmin: input.isSuperAdmin,
  };
}

export function useNav() {
  const { user, permissions } = useAuth();
  const plan = usePlan();
  const { current } = useTenant();
  const { pathname } = useLocation();
  const role = current?.pivot?.role ?? "";
  const isSuperAdmin = !!user?.is_super_admin;

  const areas = useMemo(
    () => visibleNav(REGISTRY, navContext({ has: plan.has, planLoading: plan.loading, permissions, role, isSuperAdmin })),
    [plan.has, plan.loading, permissions, role, isSuperAdmin],
  );
  const active = useMemo(() => activeItem(areas, pathname), [areas, pathname]);
  const activeArea = active?.area ?? areas[0] ?? null;
  const isAdmin = isSuperAdmin || role === "admin" || role === "owner";

  return { areas, active, activeArea, isAdmin };
}
